import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Users, Factory, Truck,
  FlaskConical, ClipboardCheck, PackageCheck,
  ChevronRight, Award, Microscope, Gauge
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';
const SECTION_BG = '#ECEEF2';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } }
};
const stagger = { show: { transition: { staggerChildren: 0.09 } } };

/* ── 版块一：制造能力 4项 ── */
const mfgItems = [
  {
    icon: ShieldCheck,
    label: 'COMPLIANCE',
    title: '企业资质与合规体系',
    body: '制造基地全面贯标执行 ISO 9001 质量管理体系认证。全系核心产品严格按照 ISO 及 ASTM 等国际主流标准体系生产与测试，具备完善的出口资质，产品全面满足欧盟、美国、澳大利亚等严苛市场的合规要求。',
    tags: ['ISO 9001', 'ASTM', 'CE'],
    img: IMAGES.CAP_ISO9001_CERTIFICATE_20260510_021917_307 as string | null,
    imgAlt: 'ISO 9001 质量管理体系认证证书',
    imgCaption: 'ISO 9001 认证证书',
  },
  {
    icon: Users,
    label: 'R&D TEAM',
    title: '研发与技术保障团队',
    body: '核心技术顾问团队直接参与制造基地配方研发与品控体系建设。技术人员不仅负责原料配方设计与生产工艺把控，更能将制造端的技术积累直接转化为面向客户端的专业选型建议与定制支持。',
    tags: ['配方研发', '工艺把控', '选型咨询'],
    img: IMAGES.CAP_RD_TEAM_20260510_021918_308 as string | null,
    imgAlt: '橡胶配方工程师团队审阅配方数据与技术文件',
    imgCaption: '配方研发技术团队',
  },
  {
    icon: Factory,
    label: 'PLANT & EQUIPMENT',
    title: '规模化厂房与机器设备',
    body: '制造基地拥有标准化的现代生产车间，配备了完善的重型橡胶加工设备集群：从精密投料的密炼机群，到高精度的压延机、大型鼓式硫化机与平板硫化机列，确保各类特种橡胶板材均能实现高效量产。',
    tags: ['密炼机', '压延机', '鼓式硫化机', '平板硫化机'],
    img: IMAGES.CAP_FACTORY_PLANT_20260510_021918_309 as string | null,
    imgAlt: '现代化橡胶板材制造基地车间 —— 鼓式硫化机与压延机生产线全景',
    imgCaption: '制造基地生产车间',
  },
  {
    icon: Truck,
    label: 'DELIVERY',
    title: '稳定可靠的最终交付能力',
    body: '凭借成熟的生产工艺与制造基地的产能优势，我们实现了标准化产品的现货快速响应与非标定制件的优先排产，确保每一单项目全周期的准时、稳定交付。',
    tags: ['标准品 3–7天', '定制品 10–20个工作日'],
    img: IMAGES.CAP_DELIVERY_WAREHOUSE_20260510_021917_310 as string | null,
    imgAlt: '橡胶板材成品仓储与装柜出口发货现场',
    imgCaption: '成品仓储与出货装柜',
  },
];

/* ── 版块二：服务能力 3项 ── */
const serviceItems = [
  {
    icon: Microscope,
    label: 'TESTING LAB',
    title: '有据可依的技术与测试服务',
    body: '制造基地建有独立材料检测实验室，配备门尼粘度测试仪、电子拉力测试机、恒温油浴测试仪及高低温老化测试箱等全套设备。能够基于客户具体的应用工况提供严谨的测试数据。',
    tags: ['门尼粘度仪', '电子拉力机', '恒温油浴仪', '老化测试箱'],
    img: IMAGES.CAP_TESTING_LAB_20260510_021917_311 as string | null,
    imgAlt: '独立材料检测实验室 —— 电子拉力测试机夹持橡胶样条进行拉伸测试',
    imgCaption: '材料检测实验室',
  },
  {
    icon: ClipboardCheck,
    label: 'QC PROCESS',
    title: '100% 可追溯的三段式质检流程',
    body: '执行系统化品控机制，质量管控覆盖原料进厂检验、半成品硫化参数监控、成品出厂物理性能核测三个核心环节。不达标批次坚决不予放行，确保客户端收到的每一卷材料性能一致。',
    tags: ['进料检验', '过程监控', '出厂核测'],
    img: IMAGES.CAP_QC_INSPECTION_20260510_021917_312 as string | null,
    imgAlt: '橡胶板材质量控制检验 —— 检测工具对橡胶样品进行规格核测',
    imgCaption: '成品质量检验',
  },
  {
    icon: PackageCheck,
    label: 'LOGISTICS',
    title: '高效响应的全球化物流能力',
    body: '制造基地距北京国际机场约 120 公里，距天津新港（Xingang Port）约 100 公里。得天独厚的区位优势结合成熟的海空货运网络，为全球数十个国家的采购商提供极具时效性的综合物流交货保障。',
    tags: ['距天津港 ~100km', '距北京机场 ~120km', '10+ 国家出口'],
    img: IMAGES.CAP_LOGISTICS_PORT_20260510_021918_313 as string | null,
    imgAlt: '天津新港集装箱码头 —— 货轮与龙门吊装载出口货物',
    imgCaption: '天津港全球物流出口',
  },
];

/* ── 关键数据栏 ── */
const keyStats = [
  { value: '20+', label: '年制造经验', sub: 'Years of Manufacturing' },
  { value: 'ISO 9001', label: '质量认证', sub: 'Quality Management' },
  { value: '10+', label: '出口国家地区', sub: 'Countries & Regions' },
  { value: '100%', label: '全程质量追溯', sub: 'Full Traceability' },
];

/* ── 三段式质检流程步骤 ── */
const qcSteps = [
  { step: '01', label: '原料进厂检验', desc: '门尼粘度、密度、外观规格全检', icon: FlaskConical },
  { step: '02', label: '硫化参数监控', desc: '温度曲线、压力曲线、时间窗口实时记录', icon: Gauge },
  { step: '03', label: '成品出厂核测', desc: '拉伸强度、断裂伸长率、硬度三项出厂强检', icon: Award },
];

/* ── 组件：Section Title ── */
function SectionTitle({ en, cn, desc }: { en: string; cn: string; desc: string }) {
  return (
    <motion.div variants={fadeUp} className="mb-14">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-0.5" style={{ background: ORANGE }} />
        <span className="font-mono text-xs font-black tracking-[0.22em] uppercase" style={{ color: ORANGE }}>{en}</span>
      </div>
      <h2 className="text-3xl lg:text-4xl font-black mb-4">{cn}</h2>
      <p className="text-sm leading-relaxed max-w-2xl" style={{ color: MUTED_TEXT }}>{desc}</p>
    </motion.div>
  );
}

/* ── 组件：Tag Badge ── */
function Tag({ label }: { label: string }) {
  return (
    <span
      className="px-2.5 py-0.5 text-xs font-mono font-bold rounded-sm"
      style={{ background: 'oklch(0.66 0.19 42 / 0.10)', color: 'oklch(0.78 0.12 42)', border: '1px solid oklch(0.66 0.19 42 / 0.25)' }}
    >
      {label}
    </span>
  );
}

/* ── 组件：图片占位符 ── */
function ImgPlaceholder({ src, alt, caption }: { src: string | null; alt: string | null; caption: string | null }) {
  if (src) {
    return (
      <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
        <img src={src} alt={alt ?? ''} className="w-full h-44 object-cover" style={{ filter: 'brightness(0.88) contrast(1.08)' }} />
        {caption && (
          <div className="px-3 py-1.5 text-xs font-mono" style={{ background: '#F0F2F5', color: MUTED_TEXT }}>
            <span style={{ color: ORANGE }}>▸</span> {caption}
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      className="rounded flex flex-col items-center justify-center h-44 text-xs font-mono"
      style={{ background: '#ECEEF2', border: `1px dashed ${BORDER}`, color: MUTED_TEXT }}
    >
      <div className="w-8 h-8 mb-2 rounded flex items-center justify-center" style={{ background: CARD_BG }}>
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      </div>
      <span className="text-center px-4 leading-relaxed" style={{ color: 'oklch(0.44 0.014 225)' }}>
        {alt ?? 'IMAGE PLACEHOLDER'}
      </span>
    </div>
  );
}

/* ── 组件：证书堆叠展示 ── */
function CertStackDisplay() {
  const certs = [
    {
      title: '质量管理体系认证证书',
      titleEn: 'Quality Management System Certificate',
      standard: 'ISO 9001:2015',
      orgZh: '中国质量认证中心',
      orgEn: 'China Quality Certification Centre',
      certNo: 'CN-QMS-2024-XXXXXXX',
      validity: '2024.06 – 2027.06',
      seal: true,
      bg: '#FAFAFA',
      accent: '#8B1A1A',
    },
    {
      title: '出口商品质量许可证',
      titleEn: 'Export Product Quality License',
      standard: 'GB/T 19001',
      orgZh: '国家认证认可监督管理委员会',
      orgEn: 'CNCA',
      certNo: 'EXP-2024-XXXXXXX',
      validity: '2024 – 2027',
      seal: false,
      bg: '#F5F8FF',
      accent: '#1A3A8B',
    },
    {
      title: '企业标准备案证书',
      titleEn: 'Enterprise Standard Filing Certificate',
      standard: 'ASTM / ISO',
      orgZh: '河北省市场监督管理局',
      orgEn: 'Hebei SAMR',
      certNo: 'Q/HEB-2024-XXXXXXX',
      validity: '2024 – 2026',
      seal: false,
      bg: '#F8FFF5',
      accent: '#1A6B2A',
    },
  ];

  return (
    <div className="w-full h-full flex items-center justify-center p-4" style={{ background: '#ECEEF2' }}>
      <div className="relative" style={{ width: 260, height: 220 }}>
        {/* 第3张 - 最底层，旋转最大 */}
        <div
          className="absolute rounded shadow-xl"
          style={{
            width: 220,
            height: 160,
            top: 36,
            left: 26,
            background: certs[2].bg,
            transform: 'rotate(6deg)',
            transformOrigin: 'bottom left',
            border: `2px solid ${certs[2].accent}`,
            opacity: 0.7,
          }}
        >
          <div className="p-3">
            <div className="text-center text-xs font-bold" style={{ color: certs[2].accent, fontFamily: 'serif', letterSpacing: '0.05em' }}>
              {certs[2].title}
            </div>
          </div>
        </div>

        {/* 第2张 - 中间层，轻微旋转 */}
        <div
          className="absolute rounded shadow-xl"
          style={{
            width: 220,
            height: 160,
            top: 20,
            left: 14,
            background: certs[1].bg,
            transform: 'rotate(3deg)',
            transformOrigin: 'bottom left',
            border: `2px solid ${certs[1].accent}`,
            opacity: 0.85,
          }}
        >
          <div className="p-3">
            <div className="text-center text-xs font-bold" style={{ color: certs[1].accent, fontFamily: 'serif', letterSpacing: '0.05em' }}>
              {certs[1].title}
            </div>
          </div>
        </div>

        {/* 第1张 - 最顶层，ISO 9001，完整展示 */}
        <div
          className="absolute rounded shadow-2xl"
          style={{
            width: 228,
            height: 168,
            top: 4,
            left: 2,
            background: certs[0].bg,
            border: `3px solid ${certs[0].accent}`,
          }}
        >
          {/* 顶部红色横幅 */}
          <div className="py-1.5 text-center" style={{ background: certs[0].accent }}>
            <div className="text-white text-xs font-black tracking-widest" style={{ fontFamily: 'serif' }}>认 证 证 书</div>
            <div className="text-white" style={{ fontSize: 9, opacity: 0.85 }}>CERTIFICATE</div>
          </div>

          {/* 证书主体 */}
          <div className="px-4 py-2 flex flex-col items-center text-center">
            <div className="font-black mb-1" style={{ color: certs[0].accent, fontSize: 13, fontFamily: 'serif', letterSpacing: '0.04em' }}>
              {certs[0].title}
            </div>
            <div className="mb-1.5" style={{ color: '#333', fontSize: 10, fontFamily: 'serif', letterSpacing: '0.08em', fontStyle: 'italic' }}>
              {certs[0].titleEn}
            </div>

            {/* ISO标准徽章 */}
            <div
              className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full mb-1.5"
              style={{ background: certs[0].accent, color: 'white', fontSize: 10, fontWeight: 900, letterSpacing: '0.1em' }}
            >
              {certs[0].standard}
            </div>

            {/* 认证机构 */}
            <div style={{ color: '#555', fontSize: 9, lineHeight: 1.4 }}>
              <div className="font-bold">{certs[0].orgZh}</div>
              <div>{certs[0].orgEn}</div>
            </div>

            {/* 有效期 */}
            <div className="mt-1 flex items-center gap-3" style={{ fontSize: 8.5, color: '#666' }}>
              <span>证书编号：{certs[0].certNo}</span>
            </div>
            <div style={{ fontSize: 8.5, color: '#888' }}>有效期：{certs[0].validity}</div>
          </div>

          {/* 底部分割线 */}
          <div style={{ height: 1, background: certs[0].accent, opacity: 0.3, margin: '0 12px' }} />

          {/* 印章占位 */}
          <div className="flex justify-end px-3 py-1">
            <div
              className="rounded-full border-2 flex items-center justify-center"
              style={{ width: 36, height: 36, borderColor: certs[0].accent, opacity: 0.4 }}
            >
              <div className="text-center" style={{ color: certs[0].accent, fontSize: 6, lineHeight: 1.2, fontWeight: 900 }}>
                <div>认证</div>
                <div>印章</div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部标注 */}
        <div
          className="absolute bottom-0 left-0 right-0 text-center font-mono"
          style={{ fontSize: 9, color: '#5A6780' }}
        >
          ▸ ISO 9001:2015 认证 · 及其他企业资质证书
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
const Capabilities = () => {
  return (
    <div className="min-h-screen text-foreground" style={{ background: '#F7F8FA' }}>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden" style={{ minHeight: 420 }}>
        <img
          src={IMAGES.FACTORY_5}
          alt="Manufacturing Facility"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.14 }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,30,53,0.90) 0%, rgba(15,30,53,0.65) 100%)' }}
        />
        <div className="relative container mx-auto px-4 py-20 lg:py-28">
          <motion.div initial="hidden" animate="show" variants={stagger} className="text-center">
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-0.5" style={{ background: ORANGE }} />
              <span className="font-mono text-xs font-black tracking-[0.22em] uppercase" style={{ color: ORANGE }}>
                MANUFACTURING &amp; SERVICE CAPABILITIES
              </span>
              <div className="w-10 h-0.5" style={{ background: ORANGE }} />
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-4xl lg:text-6xl font-black mb-5 leading-tight">
              制造与服务能力
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base lg:text-lg leading-relaxed mx-auto max-w-2xl" style={{ color: MUTED_TEXT }}>
              工程级标准驱动 · 全链路质量追溯 · 全球化交付响应
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── 关键数据横幅 ── */}
      <div style={{ background: '#1A2337', borderTop: `1px solid #2A3A5A`, borderBottom: `1px solid #2A3A5A` }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x" style={{ '--tw-divide-color': BORDER } as React.CSSProperties}>
            {keyStats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="px-6 py-6 text-center"
              >
                <div className="text-2xl lg:text-3xl font-black font-mono mb-1" style={{ color: ORANGE }}>{s.value}</div>
                <div className="text-sm font-bold mb-0.5" style={{ color: '#FFFFFF' }}>{s.label}</div>
                <div className="text-xs font-mono" style={{ color: '#9DAFC6' }}>{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════
          版块一：卓越制造
      ══════════════════════════════ */}
      <section className="py-20 lg:py-28" style={{ background: SECTION_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <SectionTitle
              en="SECTION 01 — MANUFACTURING EXCELLENCE"
              cn="卓越制造：以工程级标准驱动的量产与交付体系"
              desc="我们深知，工业级橡胶板材的核心价值在于复杂工况下的批次稳定性。依托深耕行业逾二十年的实体制造根基，我们建立了一套从原料到成品的标准化生产规模。"
            />

            {/* 4-item grid: 2列×2行，上图下文卡片布局 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mfgItems.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.label}
                    variants={fadeUp}
                    className="rounded overflow-hidden flex flex-col"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    {/* 图片区：固定高度 */}
                    {idx === 0 ? (
                      /* 企业资质：证书堆叠占位，固定高度 */
                      <div className="relative overflow-hidden" style={{ height: 240, background: '#ECEEF2' }}>
                        <CertStackDisplay />
                      </div>
                    ) : item.img ? (
                      <div className="relative overflow-hidden" style={{ height: 240 }}>
                        <img
                          src={item.img}
                          alt={item.imgAlt ?? item.title}
                          className="w-full h-full object-cover"
                          style={{ filter: 'brightness(0.82) contrast(1.1)', opacity: 0.92 }}
                        />
                        <div
                          className="absolute bottom-0 left-0 right-0 px-4 py-2 text-xs font-mono"
                          style={{ background: 'rgba(15,30,53,0.75)', color: '#D0D5E2' }}
                        >
                          <span style={{ color: ORANGE }}>▸</span> {item.imgCaption}
                        </div>
                      </div>
                    ) : (
                      <div
                        className="flex flex-col items-center justify-center text-xs font-mono"
                        style={{ height: 240, background: '#ECEEF2', borderBottom: `1px solid ${BORDER}` }}
                      >
                        <div className="w-10 h-10 rounded mb-3 flex items-center justify-center" style={{ background: CARD_BG, border: `1px dashed ${BORDER}` }}>
                          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: MUTED_TEXT }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                          </svg>
                        </div>
                          <span className="text-center px-6 leading-relaxed" style={{ color: MUTED_TEXT }}>
                          {item.imgAlt ?? 'IMAGE PLACEHOLDER'}
                        </span>
                      </div>
                    )}

                    {/* 文字区：在图片下方，内容自然铺开 */}
                    <div className="p-6 flex flex-col flex-1">
                      {/* 序号+标签 */}
                      <div className="flex items-center gap-3 mb-4">
                        <span
                          className="w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0"
                          style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: '1px solid oklch(0.66 0.19 42 / 0.35)' }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: ORANGE }} />
                        </span>
                        <span className="font-mono text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>
                          {item.label}
                        </span>
                        <span className="font-mono text-xs ml-auto tabular-nums" style={{ color: '#8A9AB5' }}>
                          0{idx + 1}
                        </span>
                      </div>

                      <h3 className="text-lg font-black mb-3">{item.title}</h3>
                      <p className="text-sm leading-relaxed mb-4 flex-1" style={{ color: MUTED_TEXT }}>{item.body}</p>
                      <div className="flex flex-wrap gap-2 mt-auto">
                        {item.tags.map((t) => <Tag key={t} label={t} />)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════
          三段式质检流程 Timeline
      ══════════════════════════════ */}
      <section className="py-16 lg:py-20" style={{ background: '#1A2337', borderTop: `1px solid #2A3A5A` }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-3">
              <div className="w-8 h-0.5" style={{ background: ORANGE }} />
              <span className="font-mono text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>
                THREE-STAGE QC FLOW
              </span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl lg:text-3xl font-black mb-10" style={{ color: '#FFFFFF' }}>
              三段式全程质量管控体系
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 relative">
              {/* connector line — desktop only */}
              <div
                className="hidden md:block absolute top-10 left-[16.666%] right-[16.666%] h-px"
                style={{ background: `linear-gradient(90deg, transparent, ${BORDER}, ${BORDER}, transparent)` }}
              />
              {qcSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.step}
                    variants={fadeUp}
                    className="flex flex-col items-center text-center px-6 py-8 relative"
                    style={{ borderRight: i < 2 ? `1px solid ${BORDER}` : undefined }}
                  >
                    {/* 步骤圆圈 */}
                    <div
                      className="w-20 h-20 rounded-full flex flex-col items-center justify-center mb-5 relative z-10"
                      style={{ background: 'oklch(0.66 0.19 42 / 0.10)', border: `2px solid oklch(0.66 0.19 42 / 0.40)` }}
                    >
                      <Icon className="w-7 h-7 mb-0.5" style={{ color: ORANGE }} />
                      <span className="font-mono text-xs font-black" style={{ color: ORANGE }}>{step.step}</span>
                    </div>
                    <h4 className="text-base font-black mb-2" style={{ color: '#FFFFFF' }}>{step.label}</h4>
                    <p className="text-xs leading-relaxed" style={{ color: '#9DAFC6' }}>{step.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════
          版块二：全链路服务
      ══════════════════════════════ */}
      <section className="py-20 lg:py-28" style={{ background: SECTION_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <SectionTitle
              en="SECTION 02 — END-TO-END SERVICE"
              cn="全链路服务：用数据与流程保障每一寸产品的可靠性"
              desc="我们致力于提供『非一次性交易』的长期合作导向，将技术赋能贯穿于售前选型、售中质检到售后物流的全生命周期。"
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {serviceItems.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.label}
                    variants={fadeUp}
                    className="rounded overflow-hidden flex flex-col"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    {/* 图片 */}
                    <ImgPlaceholder src={item.img} alt={item.imgAlt} caption={item.imgCaption} />

                    {/* 内容 */}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-4">
                        <span
                          className="w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0"
                          style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: '1px solid oklch(0.66 0.19 42 / 0.35)' }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: ORANGE }} />
                        </span>
                        <span className="font-mono text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>
                          {item.label}
                        </span>
                        <span className="font-mono text-xs ml-auto tabular-nums" style={{ color: '#8A9AB5' }}>
                          0{idx + 1}
                        </span>
                      </div>
                      <h3 className="text-base font-black mb-3">{item.title}</h3>
                      <p className="text-sm leading-relaxed mb-4 flex-1" style={{ color: MUTED_TEXT }}>{item.body}</p>
                      <div className="flex flex-wrap gap-1.5 mt-auto">
                        {item.tags.map((t) => <Tag key={t} label={t} />)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 地理区位示意条 ── */}
      <section className="py-10" style={{ background: '#1A2337', borderTop: `1px solid #2A3A5A`, borderBottom: `1px solid #2A3A5A` }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-8 text-center"
          >
            <div>
              <span className="font-mono text-xs font-black tracking-widest uppercase block mb-1" style={{ color: ORANGE }}>LOCATION</span>
              <span className="text-sm font-bold" style={{ color: '#FFFFFF' }}>河北省沧州市橡胶产业基地</span>
            </div>
            <div className="h-8 w-px hidden md:block" style={{ background: BORDER }} />
            <div>
              <span className="font-mono text-2xl font-black" style={{ color: ORANGE }}>~100<span className="text-sm">km</span></span>
              <span className="text-xs block font-bold mt-0.5" style={{ color: '#FFFFFF' }}>天津新港 Xingang Port</span>
            </div>
            <div className="h-8 w-px hidden md:block" style={{ background: BORDER }} />
            <div>
              <span className="font-mono text-2xl font-black" style={{ color: ORANGE }}>~120<span className="text-sm">km</span></span>
              <span className="text-xs block font-bold mt-0.5" style={{ color: '#FFFFFF' }}>北京国际机场 PEK</span>
            </div>
            <div className="h-8 w-px hidden md:block" style={{ background: BORDER }} />
            <div>
              <span className="font-mono text-2xl font-black" style={{ color: ORANGE }}>10+</span>
              <span className="text-xs block font-bold mt-0.5" style={{ color: '#FFFFFF' }}>出口国家和地区</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20" style={{ background: '#F7F8FA' }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center"
          >
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-4">
              <div className="w-6 h-0.5" style={{ background: ORANGE }} />
              <span className="font-mono text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>
                START COOPERATION
              </span>
              <div className="w-6 h-0.5" style={{ background: ORANGE }} />
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-black mb-4">
              获取制造基地能力评估报告
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm mb-10 max-w-xl mx-auto" style={{ color: MUTED_TEXT }}>
              告知您的应用工况与采购规格，我们的技术团队将在 24 小时内提供专项选型建议与制造基地能力说明。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm transition-all hover:opacity-85"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                联系技术团队
                <ChevronRight className="w-4 h-4" />
              </Link>
              <Link
                to={ROUTE_PATHS.GUIDE}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm transition-all hover:opacity-85"
                style={{ border: `1px solid ${BORDER}`, color: '#1A2337' }}
              >
                查看选材指南
                <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Capabilities;
